import * as yup from 'yup';
import { N_YEARS } from './years';

export const fields = [
  'id',
  'name',
  'unitCost',
  'expectedLifespanInHours',
  'quantity',
  'assignment',
  'requiresFuel',
  'gallonsFuelPerHour',
  'isImplement',
  'weightClass',
  'depreciates',
  'purchaseYear',
  'updatedAt',
] as const;

export const pricingFields = ['id', 'name'] as const;

export const bulkEditFields = [
  'unitCost',
  'quantity',
  'purchaseYear',
  'assignment',
] as const;

export const ASSIGNMENTS = [
  { value: 'owner', name: 'Owner' },
  { value: 'developer', name: 'Developer' },
] as const;

export const IMPLEMENT_WEIGHT_CLASSES = [
  { value: 'light', name: 'Light' },
  { value: 'medium', name: 'Medium' },
  { value: 'heavy', name: 'Heavy' },
] as const;

const unitCostSchema = yup
  .number()
  .min(0, 'Minimum cost is $0')
  .required('Required');

const quantitySchema = yup
  .number()
  .integer('Must be a whole number')
  .positive('Must be at least 1')
  .required('Required');

const purchaseYearSchema = yup
  .number()
  .integer('Invalid year')
  .min(1, 'Must be at least 1')
  .max(N_YEARS, `Must be ${N_YEARS} or less`)
  .required('Required');

export const bulkCapexSchema = yup.object().shape({
  unitCost: unitCostSchema,
  quantity: quantitySchema,
  purchaseYear: purchaseYearSchema,
});

export const fullCapexSchema = yup.object().shape({
  name: yup.string().required('Required'),
  unitCost: unitCostSchema,
  expectedLifespanInHours: yup.number().when('depreciates', {
    is: true,
    then: (schema) =>
      schema.required('Required').positive('Must be greater than 0'),
    otherwise: (schema) => schema.nullable(),
  }),
  quantity: quantitySchema,
  requiresFuel: yup.boolean().required('Required'),
  gallonsFuelPerHour: yup
    .number()
    .typeError('Required') // If it's not a number, it's probably null
    .positive('Must be positive')
    .nullable()
    .when('requiresFuel', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) =>
        schema.test({
          name: 'is-null',
          message: 'Must not include gallons per hour if does not require fuel',
          test: (value) => value == null,
        }),
    }),
  pricing: yup
    .object()
    .nullable()
    .when('requiresFuel', {
      is: true,
      then: (schema) => schema.required('Required'), // override message that shows "pricing"
      otherwise: (schema) =>
        schema.test({
          name: 'is-null',
          message: 'Must not include pricing if does not require fuel',
          test: (value) => value == null,
        }),
    }),
  isImplement: yup
    .boolean()
    .required()
    .when('requiresFuel', {
      is: true,
      then: (schema) =>
        schema.test({
          name: 'is-false',
          message: 'Cannot be implement if requires fuel',
          test: (value) => value === false,
        }),
    }),
  weightClass: yup
    .string()
    .nullable()
    .when('isImplement', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) =>
        schema.test({
          name: 'is-null',
          message: 'Must not include weight class if is not implement',
          test: (value) => value == null,
        }),
    }),
  depreciates: yup.boolean().required('Required'),
  purchaseYear: purchaseYearSchema,
  reference: yup.string().nullable(),
  calculationExplanation: yup.string().nullable(),
});
